import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, Award, RefreshCw, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';

// Perguntas da avaliação
const questions = [
  {
    id: 1,
    question: "Quem foi o primeiro rei de Israel?",
    options: ["Davi", "Saul", "Salomão", "Josué"],
    correctAnswer: 1,
    explanation: "Saul foi o primeiro rei de Israel, ungido pelo profeta Samuel."
  },
  {
    id: 2,
    question: "Qual livro da Bíblia contém os Dez Mandamentos?",
    options: ["Gênesis", "Êxodo", "Levítico", "Deuteronômio"],
    correctAnswer: 1,
    explanation: "Os Dez Mandamentos são encontrados em Êxodo 20:1-17."
  },
  {
    id: 3,
    question: "Quem escreveu a maior parte dos Salmos?",
    options: ["Moisés", "Salomão", "Davi", "Asafe"],
    correctAnswer: 2,
    explanation: "Davi escreveu aproximadamente 73 dos 150 Salmos."
  },
  {
    id: 4,
    question: "Qual profeta foi levado ao céu em um redemoinho?",
    options: ["Elias", "Eliseu", "Isaías", "Jeremias"],
    correctAnswer: 0,
    explanation: "Elias foi arrebatado ao céu em um redemoinho, conforme 2 Reis 2:11."
  },
  {
    id: 5,
    question: "Quem negou Jesus três vezes antes do galo cantar?",
    options: ["João", "Pedro", "Tiago", "André"],
    correctAnswer: 1,
    explanation: "Pedro negou Jesus três vezes, como Jesus havia predito."
  },
  {
    id: 6,
    question: "Qual foi o primeiro milagre de Jesus registrado na Bíblia?",
    options: ["Cura de um leproso", "Transformar água em vinho", "Multiplicação dos pães", "Andar sobre as águas"],
    correctAnswer: 1,
    explanation: "Jesus transformou água em vinho nas bodas de Caná, conforme João 2:1-11."
  },
  {
    id: 7,
    question: "Quem foi jogado na cova dos leões?",
    options: ["Daniel", "Jonas", "Jeremias", "Ezequiel"],
    correctAnswer: 0,
    explanation: "Daniel foi lançado na cova dos leões por continuar orando a Deus, conforme Daniel 6."
  },
  {
    id: 8,
    question: "Qual apóstolo era cobrador de impostos antes de seguir Jesus?",
    options: ["Pedro", "João", "Mateus", "Filipe"],
    correctAnswer: 2,
    explanation: "Mateus (também chamado Levi) era cobrador de impostos antes de seguir Jesus."
  },
  {
    id: 9,
    question: "Quem foi o primeiro mártir cristão mencionado no livro de Atos?",
    options: ["Tiago", "Estêvão", "Pedro", "Paulo"],
    correctAnswer: 1,
    explanation: "Estêvão foi o primeiro mártir cristão, conforme registrado em Atos 7."
  },
  {
    id: 10,
    question: "Qual cidade foi destruída junto com Sodoma?",
    options: ["Nínive", "Jericó", "Gomorra", "Babilônia"],
    correctAnswer: 2,
    explanation: "Gomorra foi destruída junto com Sodoma, conforme Gênesis 19."
  }
];

export default function CursoAvaliacao() {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answers, setAnswers] = useState<(number | null)[]>(new Array(questions.length).fill(null));
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [timerActive, setTimerActive] = useState(true);

  const currentQuestion = questions[currentQuestionIndex];
  const passingScore = 70; // 70% passing threshold
  const isPassed = (score / questions.length) * 100 >= passingScore;

  useEffect(() => {
    // Carregar resultados salvos, se existirem
    const loadResults = async () => {
      try {
        const savedResults = localStorage.getItem('courseAssessmentResults');
        if (savedResults) {
          const { answers: savedAnswers, score: savedScore, completed } = JSON.parse(savedResults);
          if (completed) {
            setAnswers(savedAnswers);
            setScore(savedScore);
            setQuizCompleted(true);
          }
        }
      } catch (error) {
        console.error('Error loading results:', error);
      }
    };

    loadResults();
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (timerActive && timeLeft > 0) {
      timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0 && timerActive) {
      handleTimeUp();
    }
    return () => clearTimeout(timer);
  }, [timeLeft, timerActive]);

  const handleTimeUp = () => {
    setTimerActive(false);
    setShowAnswer(true);
    
    // Marcar como resposta incorreta
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = -1; // -1 indica que o tempo acabou
    setAnswers(newAnswers);
  };

  const handleAnswerSelection = (answerIndex: number) => {
    if (showAnswer) return; // Prevent changing answer after showing feedback

    setSelectedAnswer(answerIndex);
    setShowAnswer(true);
    setTimerActive(false);

    // Update answers array
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setAnswers(newAnswers);

    // Update score if correct
    if (answerIndex === currentQuestion.correctAnswer) {
      setScore(prevScore => prevScore + 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(answers[currentQuestionIndex + 1]);
      setShowAnswer(answers[currentQuestionIndex + 1] !== null);
      setTimeLeft(30);
      setTimerActive(!showAnswer);
    } else if (!quizCompleted) {
      setQuizCompleted(true);
      saveResults();
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setSelectedAnswer(answers[currentQuestionIndex - 1]);
      setShowAnswer(true); // Always show answer for previous questions
      setTimerActive(false);
    }
  };

  const saveResults = () => {
    try {
      const results = {
        answers,
        score,
        completed: true,
        date: new Date().toISOString(),
        passed: isPassed
      };
      localStorage.setItem('courseAssessmentResults', JSON.stringify(results));
    } catch (error) {
      console.error('Error saving results:', error);
    }
  };

  const handleRestartQuiz = () => {
    if (window.confirm("Tem certeza que deseja reiniciar? Todo o seu progresso será perdido.")) {
      setAnswers(new Array(questions.length).fill(null));
      setCurrentQuestionIndex(0);
      setSelectedAnswer(null);
      setScore(0);
      setQuizCompleted(false);
      setShowAnswer(false);
      setTimeLeft(30);
      setTimerActive(true);
      localStorage.removeItem('courseAssessmentResults');
    }
  };

  const handleViewCertificate = () => {
    navigate('/cursos/certificado');
  };

  if (quizCompleted) {
    const percentage = Math.round((score / questions.length) * 100);
    
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white p-8 rounded-xl shadow-elevation-1">
          <div className="text-center mb-8">
            <div className="inline-block p-4 rounded-full bg-brand-light mb-4">
              <Award className="h-12 w-12 text-brand-teal" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {isPassed ? "✅ Parabéns, você concluiu o curso!" : "❌ Você não atingiu a pontuação mínima."}
            </h2>
            <p className="text-gray-600">
              Você acertou {score} de {questions.length} questões
            </p>
            <div className="mt-4">
              <div className="text-2xl font-bold text-brand-teal">
                {percentage}%
              </div>
              <p className="text-lg font-medium text-gray-700 mt-2">
                {isPassed ? "Aprovado" : "Reprovado"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="p-4 bg-brand-light rounded-lg">
              <h3 className="font-medium text-brand-teal mb-2">Estatísticas</h3>
              <ul className="space-y-2 text-gray-700">
                <li>Total de questões: {questions.length}</li>
                <li>Acertos: {score}</li>
                <li>Erros: {questions.length - score}</li>
                <li>Pontuação: {percentage}%</li>
              </ul>
            </div>
            
            <div className="p-4 bg-brand-light rounded-lg">
              <h3 className="font-medium text-brand-teal mb-2">Avaliação</h3>
              <p className="text-gray-700 mb-2">
                {percentage >= 70
                  ? "Excelente trabalho! Você demonstra um bom conhecimento bíblico."
                  : percentage >= 50
                  ? "Bom trabalho! Continue estudando a Palavra."
                  : "Continue estudando a Palavra para melhorar seu conhecimento bíblico."}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-sm text-amber-600">
                  Lembre-se: o conhecimento deve ser aplicado à vida!
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {isPassed ? (
              <>
                <button
                  onClick={handleViewCertificate}
                  className="flex-1 bg-brand-teal text-white px-6 py-3 rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center justify-center gap-2"
                >
                  <Award className="h-5 w-5" />
                  <span>Ver Certificado</span>
                </button>
                
                <button
                  onClick={handleRestartQuiz}
                  className="flex-1 border border-brand-teal text-brand-teal px-6 py-3 rounded-lg hover:bg-brand-light transition-colors"
                >
                  Refazer Avaliação
                </button>
              </>
            ) : (
              <button
                onClick={handleRestartQuiz}
                className="w-full bg-brand-teal text-white px-6 py-3 rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-5 w-5" />
                <span>Tentar Novamente</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white p-8 rounded-xl shadow-elevation-1">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-brand-teal">Avaliação Final</h1>
          <div className="flex items-center gap-2">
            <Clock className={`h-5 w-5 ${timeLeft <= 5 && timerActive ? 'text-red-500' : 'text-gray-500'}`} />
            <span className={`${timeLeft <= 5 && timerActive ? 'text-red-500 font-bold' : 'text-gray-500'}`}>
              {timeLeft}s
            </span>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-500">
              Pergunta {currentQuestionIndex + 1} de {questions.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-brand-teal h-2.5 rounded-full transition-all duration-300" 
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {currentQuestion.question}
          </h2>
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelection(index)}
                disabled={showAnswer}
                className={`w-full p-4 text-left rounded-lg transition-colors flex justify-between items-center ${
                  showAnswer
                    ? index === currentQuestion.correctAnswer
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : selectedAnswer === index
                      ? 'bg-red-100 text-red-800 border border-red-200'
                      : 'bg-gray-50 text-gray-500'
                    : selectedAnswer === index
                    ? 'bg-brand-teal text-white'
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                }`}
              >
                <span>{option}</span>
                {showAnswer && (
                  index === currentQuestion.correctAnswer ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : selectedAnswer === index ? (
                    <XCircle className="h-5 w-5 text-red-600" />
                  ) : null
                )}
              </button>
            ))}
          </div>
        </div>

        {showAnswer && (
          <div className="mb-6 p-4 bg-brand-light rounded-lg">
            <h3 className="font-medium text-brand-teal mb-2">Explicação:</h3>
            <p className="text-gray-700">
              {currentQuestion.explanation}
            </p>
          </div>
        )}

        <div className="flex justify-between">
          <button
            onClick={handlePreviousQuestion}
            disabled={currentQuestionIndex === 0}
            className="px-4 py-2 flex items-center gap-2 text-gray-600 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </button>
          <button
            onClick={handleNextQuestion}
            disabled={!showAnswer}
            className="bg-brand-teal text-white px-6 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {currentQuestionIndex === questions.length - 1 ? (
              'Finalizar'
            ) : (
              <>
                Próxima
                <ChevronRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}